import React, { useState, useEffect, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import Button from '@components/Button/Button';
import cohortsService from '@/services/cohorts/cohortsService';

interface Props {
    cohortId: string;
    scheduleList: any[];
}

const AttendanceTab: React.FC<Props> = ({ cohortId, scheduleList }) => {
    const queryClient = useQueryClient();
    
    // Flatten schedule to get all days with their dates
    const allDays = useMemo(() => {
        const days: { dayNumber: number, weekNumber: number, dateStr: string, label: string }[] = [];
        scheduleList.forEach(week => {
            week.days.forEach((day: any) => {
                if (day.date) {
                    const dateObj = new Date(day.date);
                    const dateStr = dateObj.toISOString().split('T')[0]; // YYYY-MM-DD
                    const label = `Week ${week.week_number} - Day ${day.day_number} (${dateStr})`;
                    days.push({ dayNumber: day.day_number, weekNumber: week.week_number, dateStr, label });
                }
            });
        });
        return days;
    }, [scheduleList]);

    const [selectedDate, setSelectedDate] = useState<string>(allDays[0]?.dateStr || new Date().toISOString().split('T')[0]);

    // Fetch students
    const { data: studentsRes, isLoading: isLoadingStudents } = useQuery({
        queryKey: ['cohort-students', cohortId],
        queryFn: () => cohortsService.getEnrolledStudents(cohortId),
        enabled: !!cohortId,
    });
    const students = studentsRes?.data || [];

    // Fetch attendance for selected date
    const { data: attendanceRes, isLoading: isLoadingAttendance } = useQuery({
        queryKey: ['cohort-attendance', cohortId, selectedDate],
        queryFn: () => cohortsService.getAttendance(cohortId, selectedDate),
        enabled: !!cohortId && !!selectedDate,
    });
    const existingAttendance = attendanceRes?.data || [];

    // Local state to manage attendance form
    const [attendanceRecords, setAttendanceRecords] = useState<Record<string, string>>({});

    // Sync existing attendance with local state
    useEffect(() => {
        if (students.length > 0) {
            const records: Record<string, string> = {};
            students.forEach((enrollment: any) => {
                const existing = existingAttendance.find((a: any) => a.enrollment_id === enrollment.id);
                records[enrollment.id] = existing ? existing.status : 'PRESENT'; // Default to PRESENT
            });
            setAttendanceRecords(records);
        }
    }, [students, existingAttendance, selectedDate]);

    const handleStatusChange = (enrollmentId: string, status: string) => {
        setAttendanceRecords(prev => ({ ...prev, [enrollmentId]: status }));
    };

    const saveMutation = useMutation({
        mutationFn: (payload: { date: string, records: any[] }) => cohortsService.saveAttendance(cohortId, payload.date, payload.records),
        onSuccess: () => {
            toast.success('Attendance saved successfully');
            queryClient.invalidateQueries({ queryKey: ['cohort-attendance', cohortId, selectedDate] });
        },
        onError: () => {
            toast.error('Failed to save attendance');
        }
    });

    const handleSave = () => {
        const records = Object.keys(attendanceRecords).map(enrollment_id => ({
            enrollment_id,
            status: attendanceRecords[enrollment_id]
        }));
        saveMutation.mutate({ date: selectedDate, records });
    };

    if (isLoadingStudents || isLoadingAttendance) {
        return (
            <div className="flex justify-center items-center h-48">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[var(--primary-color)]" />
            </div>
        );
    }

    return (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex flex-col md:flex-row justify-between items-center mb-6 gap-4">
                <div className="w-full md:w-1/3">
                    <label className="block text-sm font-semibold text-gray-700 mb-1">Select Day to Mark Attendance</label>
                    <select 
                        value={selectedDate}
                        onChange={(e) => setSelectedDate(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--primary-color)]"
                    >
                        {allDays.map(d => (
                            <option key={d.dateStr} value={d.dateStr}>{d.label}</option>
                        ))}
                    </select>
                </div>
                <Button 
                    onClick={handleSave} 
                    disabled={saveMutation.isPending || students.length === 0}
                    variant="primary"
                >
                    {saveMutation.isPending ? 'Saving...' : 'Save Attendance'}
                </Button>
            </div>

            {students.length === 0 ? (
                <div className="text-center py-8 text-gray-500 bg-gray-50 rounded-lg border border-dashed border-gray-200">
                    No students currently enrolled in this cohort.
                </div>
            ) : (
                <div className="overflow-x-auto rounded-xl border border-gray-200">
                    <table className="min-w-full text-left bg-white">
                        <thead className="bg-gray-50 border-b border-gray-200">
                            <tr>
                                <th className="px-4 py-3 text-sm font-semibold text-gray-600">Enrollment ID</th>
                                <th className="px-4 py-3 text-sm font-semibold text-gray-600">Student Name</th>
                                <th className="px-4 py-3 text-sm font-semibold text-gray-600">Email</th>
                                <th className="px-4 py-3 text-sm font-semibold text-gray-600 text-center">Status</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {students.map((enrollment: any) => (
                                <tr key={enrollment.id} className="hover:bg-gray-50/50 transition-colors">
                                    <td className="px-4 py-3 text-sm font-medium text-gray-800">{enrollment.enrollment_number}</td>
                                    <td className="px-4 py-3 text-sm text-gray-600">{enrollment.user.first_name} {enrollment.user.last_name}</td>
                                    <td className="px-4 py-3 text-sm text-gray-500">{enrollment.user.email}</td>
                                    <td className="px-4 py-3">
                                        <div className="flex justify-center items-center gap-3">
                                            {['PRESENT', 'ABSENT', 'LATE'].map(status => (
                                                <label key={status} className={`flex items-center gap-1.5 cursor-pointer px-2.5 py-1 rounded-md text-xs font-semibold border transition-colors
                                                    ${attendanceRecords[enrollment.id] === status 
                                                        ? (status === 'PRESENT' ? 'bg-green-50 text-green-700 border-green-200' 
                                                        : status === 'ABSENT' ? 'bg-red-50 text-red-700 border-red-200' 
                                                        : 'bg-amber-50 text-amber-700 border-amber-200')
                                                        : 'bg-white text-gray-500 border-gray-200 hover:bg-gray-50'}
                                                `}>
                                                    <input 
                                                        type="radio" 
                                                        name={`status-${enrollment.id}`} 
                                                        value={status}
                                                        checked={attendanceRecords[enrollment.id] === status}
                                                        onChange={() => handleStatusChange(enrollment.id, status)}
                                                        className="sr-only"
                                                    />
                                                    {status}
                                                </label>
                                            ))}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

export default AttendanceTab;
